import React, { useCallback, useMemo, useState } from "react";
import {
  Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Center, Flex, FormControl, FormLabel, HStack,
  Progress, Select, SimpleGrid, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack, useToast,
} from "@chakra-ui/react";
import { RefreshCw, ScanSearch } from "lucide-react";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip as ChartTooltip, Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import ConfirmDialog from "../ConfirmDialog";
import { retomarIaSaudeAnuncios, varrerSaudeAnuncios } from "../../services/api";
import {
  ACCENT, ACCENT_HOVER, AZUL, COR_BARRA, COR_CONTA, COR_SERIE, ErroCarga, Kpi, PERIODOS, SeloSomentePausados, VERDE, VERMELHO,
  Vazio, contasSomentePausadas, dataHoraBR, diaBR, iaPausada, nomeConta, num, pct, rotulo, toastDeFalha, usd, useMontado,
} from "./comum";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend);

const SUPERFICIE = "#ffffff";
const TINTA = "#4A5568";
const GRADE = "#e1e0d9";
const TAMANHO_PEQUENO = { base: "md", lg: "xs" };

const SERIES_DIA = [
  { chave: "corrigidos", label: "Corrigidos" },
  { chave: "recriados", label: "Recriados" },
  { chave: "falhas", label: "Falhas" },
  { chave: "simulados", label: "Simulados" },
];

/** Há segmento com valor acima deste na mesma barra? (só a ponta da pilha arredonda) */
const temSegmentoAcima = (ctx) => {
  const { datasets } = ctx.chart.data;
  if (!Number(datasets[ctx.datasetIndex].data[ctx.dataIndex])) return false;
  return datasets.slice(ctx.datasetIndex + 1).some((d) => Number(d.data[ctx.dataIndex]) > 0);
};

function Info({ rotulo: titulo, children }) {
  return (
    <Box minW={0}>
      <Text fontSize="2xs" color="gray.600" textTransform="uppercase" fontWeight="bold" letterSpacing="wide" noOfLines={1}>
        {titulo}
      </Text>
      <Box fontSize="sm" fontWeight="semibold" color="gray.800" mt={0.5}>{children}</Box>
    </Box>
  );
}

/** Uso contra um teto: verde, laranja a partir de 70%, vermelho a partir de 90%. */
function Medidor({ valor, maximo, rotulo: titulo }) {
  const p = maximo > 0 ? Math.min(100, (Number(valor || 0) / maximo) * 100) : 0;
  const esquema = p >= 90 ? "red" : p >= 70 ? "orange" : "green";
  return <Progress value={p} size="xs" colorScheme={esquema} bg={`${esquema}.100`} borderRadius="full" mt={1} aria-label={titulo} />;
}

/**
 * Recriações/exclusões do dia por conta, cada uma com o seu medidor contra o teto diário. O teto pode vir por
 * conta ({conta: teto}), como o da troca para coleta, que sobe em rampa.
 */
function UsoPorConta({ titulo, uso, maximo, nomesContas }) {
  const linhas = Object.entries(uso || {}).sort(([a], [b]) => Number(a) - Number(b));
  const tetoDa = (conta) => (maximo !== null && typeof maximo === "object" ? maximo[conta] : maximo);
  return (
    <Info rotulo={titulo}>
      {linhas.length === 0 ? "—" : linhas.map(([conta, n]) => (
        <Box key={conta} mt={1}>
          <Flex justify="space-between" gap={2} fontSize="xs">
            <Text noOfLines={1} minW={0}>{nomeConta(nomesContas, conta)}</Text>
            <Text flexShrink={0}>{num(n)} <Text as="span" color="gray.600" fontWeight="normal">de {num(tetoDa(conta))}</Text></Text>
          </Flex>
          <Medidor valor={n} maximo={Number(tetoDa(conta)) || 0}
            rotulo={`${titulo} na ${nomeConta(nomesContas, conta)}: ${num(n)} de ${num(tetoDa(conta))}`} />
        </Box>
      ))}
    </Info>
  );
}

function Cartao({ titulo, extra, children, ...rest }) {
  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" p={{ base: 3, md: 4 }} minW={0} {...rest}>
      {(titulo || extra) && (
        <Flex justify="space-between" align="center" gap={2} mb={3} flexWrap="wrap">
          {titulo && <Text as="h2" fontWeight="bold" fontSize="sm" color="gray.800">{titulo}</Text>}
          {extra}
        </Flex>
      )}
      {children}
    </Box>
  );
}

/** Lista de barras para categorias sem ordem (motivo, cura, tipo). Toque leva aos anúncios filtrados. */
function ListaBarras({ itens, rotuloDe, onVer, vazio = "Nada no momento.", limite = 8, descricaoVer = "ver anúncios" }) {
  const [todos, setTodos] = useState(false);
  if (!itens.length) return <Text fontSize="sm" color="gray.600">{vazio}</Text>;
  const max = Math.max(1, ...itens.map((i) => Number(i.total) || 0));
  const visiveis = todos ? itens : itens.slice(0, limite);
  return (
    <Box>
      <VStack align="stretch" spacing={0.5}>
        {visiveis.map((i) => {
          const conteudo = (
            <>
              <Flex justify="space-between" gap={2} fontSize="sm">
                <Text noOfLines={1} minW={0} color="gray.800">{rotuloDe(i.chave)}</Text>
                <Text fontWeight="semibold" flexShrink={0} color="gray.800" sx={{ fontVariantNumeric: "tabular-nums" }}>{num(i.total)}</Text>
              </Flex>
              <Box h="6px" bg="gray.100" borderRadius="full" mt={1} overflow="hidden">
                <Box h="100%" w={`${((Number(i.total) || 0) / max) * 100}%`} bg={COR_BARRA} borderRadius="full" />
              </Box>
            </>
          );
          return onVer ? (
            <Box key={i.chave} as="button" type="button" w="100%" textAlign="left" px={2} py={2} borderRadius="md"
              _hover={{ bg: "gray.50" }} _focusVisible={{ boxShadow: "outline", outline: "none" }}
              onClick={() => onVer(i.chave)} aria-label={`${rotuloDe(i.chave)}: ${num(i.total)} — ${descricaoVer}`}>
              {conteudo}
            </Box>
          ) : (
            <Box key={i.chave} px={2} py={2}>{conteudo}</Box>
          );
        })}
      </VStack>
      {itens.length > limite && (
        <Button size="sm" variant="link" colorScheme="blue" mt={{ base: 0, md: 2 }} ml={2} minH={{ base: "40px", lg: "auto" }}
          onClick={() => setTodos((v) => !v)} aria-expanded={todos}>
          {todos ? "Mostrar menos" : `Mostrar todos (${num(itens.length)})`}
        </Button>
      )}
    </Box>
  );
}

/** Parte dos itens em aberto ainda sem diagnóstico: os quadros "por motivo/cura" só contam o resto. */
function SemDiagnostico({ n, oQue, ligado }) {
  if (!n) return null;
  return (
    <Text fontSize="sm" color="gray.600" mt={2} px={2}>
      Ainda sem diagnóstico: <Text as="span" fontWeight="semibold" color="gray.800">{num(n)}</Text> {oQue}
      {ligado ? " (o ciclo lê aos poucos)." : " (a leitura só anda com o sistema ligado)."}
    </Text>
  );
}

/** Cartão de gráfico com a tabela equivalente (acessível sem cor e sem passar o mouse). */
function CartaoGrafico({ titulo, subtitulo, grafico, tabela }) {
  const [verTabela, setVerTabela] = useState(false);
  return (
    <Cartao
      titulo={titulo}
      extra={(
        <Button size={TAMANHO_PEQUENO} variant="outline" onClick={() => setVerTabela((v) => !v)}>
          {verTabela ? "Ver gráfico" : "Ver tabela"}
        </Button>
      )}
    >
      {subtitulo && <Text fontSize="xs" color="gray.600" mt={-2} mb={2}>{subtitulo}</Text>}
      {verTabela ? (
        <Box overflowX="auto" maxH="320px" overflowY="auto" borderWidth="1px" borderColor="gray.100" borderRadius="md">
          {tabela}
        </Box>
      ) : (
        <Box h={{ base: "220px", md: "280px" }}>{grafico}</Box>
      )}
    </Cartao>
  );
}

function GraficoDiario({ serie, periodo }) {
  const dados = useMemo(() => ({
    labels: serie.map((d) => diaBR(d.dia)),
    datasets: SERIES_DIA.map(({ chave, label }) => ({
      label,
      data: serie.map((d) => Number(d[chave] || 0)),
      backgroundColor: COR_SERIE[chave],
      // 2px da própria superfície separam um segmento do de cima.
      borderColor: SUPERFICIE,
      borderSkipped: false,
      borderWidth: (ctx) => ({ top: temSegmentoAcima(ctx) ? 2 : 0, right: 0, bottom: 0, left: 0 }),
      borderRadius: (ctx) => (temSegmentoAcima(ctx) ? 0 : { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 }),
      maxBarThickness: 24,
    })),
  }), [serie]);

  const opcoes = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 10, boxHeight: 10, color: TINTA, font: { size: 11 } } },
      tooltip: {
        filter: (item) => item.parsed.y > 0,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${num(ctx.parsed.y)}`,
          footer: (itens) => {
            if (!itens.length) return "";
            const custo = Number(serie[itens[0].dataIndex]?.custo_ia_usd || 0);
            return custo > 0 ? `Custo de IA: ${usd(custo)}` : "";
          },
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, border: { color: GRADE }, ticks: { color: TINTA, maxRotation: 0, autoSkip: true, maxTicksLimit: 10, font: { size: 10 } } },
      y: { stacked: true, beginAtZero: true, grid: { color: GRADE }, border: { display: false }, ticks: { color: TINTA, precision: 0, font: { size: 10 }, callback: (v) => num(v) } },
    },
  }), [serie]);

  const descricao = useMemo(() => {
    const soma = (chave) => serie.reduce((t, d) => t + Number(d[chave] || 0), 0);
    return `Gráfico de barras das correções por dia nos últimos ${periodo} dias: ${num(soma("corrigidos"))} corrigidos, `
      + `${num(soma("recriados"))} recriados, ${num(soma("falhas"))} falhas e ${num(soma("simulados"))} simulações. `
      + "Os números de cada dia estão em “Ver tabela”.";
  }, [serie, periodo]);

  return <Bar data={dados} options={opcoes} role="img" aria-label={descricao} />;
}

/** Uma linha por conta, cor fixa por conta (segue a conta, não a posição). */
function useGraficoForaDoAr(serie, nomesContas) {
  const chaves = useMemo(() => {
    const todas = new Set();
    serie.forEach((p) => Object.keys(p).forEach((k) => { if (/^conta_\d+$/.test(k)) todas.add(k); }));
    return [...todas].sort((a, b) => Number(a.slice(6)) - Number(b.slice(6)));
  }, [serie]);

  const nomeDaChave = useCallback((chave) => nomeConta(nomesContas, Number(chave.slice(6))), [nomesContas]);

  const dados = useMemo(() => ({
    labels: serie.map((d) => diaBR(d.dia)),
    datasets: chaves.map((chave) => {
      const cor = COR_CONTA[(Number(chave.slice(6)) - 1) % COR_CONTA.length] || COR_CONTA[0];
      return {
        label: nomeDaChave(chave),
        data: serie.map((d) => (d[chave] === null || d[chave] === undefined ? null : Number(d[chave]))),
        borderColor: cor,
        backgroundColor: cor,
        borderWidth: 2,
        tension: 0.25,
        // Com muitos dias o ponto (e o anel branco dele) picotaria a linha: só aparece no hover.
        pointRadius: serie.length > 14 ? 0 : 3,
        pointHoverRadius: 5,
        pointBorderColor: SUPERFICIE,
        pointBorderWidth: 2,
        spanGaps: true,
      };
    }),
  }), [serie, chaves, nomeDaChave]);

  const opcoes = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, boxHeight: 2, color: TINTA, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${num(ctx.parsed.y)}` } },
    },
    scales: {
      x: { grid: { display: false }, border: { color: GRADE }, ticks: { color: TINTA, maxRotation: 0, autoSkip: true, maxTicksLimit: 10, font: { size: 10 } } },
      // Linha não precisa partir do zero: a escala justa deixa a tendência (subindo ou caindo) visível.
      y: { grace: "10%", grid: { color: GRADE }, border: { display: false }, ticks: { color: TINTA, precision: 0, font: { size: 10 }, callback: (v) => num(v) } },
    },
  }), []);

  const descricao = useMemo(() => {
    const partes = chaves.map((chave) => {
      const pontos = serie.filter((d) => d[chave] !== null && d[chave] !== undefined);
      if (!pontos.length) return null;
      const primeiro = pontos[0];
      const ultimo = pontos[pontos.length - 1];
      return `${nomeDaChave(chave)}: de ${num(primeiro[chave])} em ${diaBR(primeiro.dia)} para ${num(ultimo[chave])} em ${diaBR(ultimo.dia)}`;
    }).filter(Boolean);
    return `Gráfico de linhas dos anúncios fora do ar por conta. ${partes.join("; ")}.`;
  }, [serie, chaves, nomeDaChave]);

  return { chaves, nomeDaChave, dados, opcoes, descricao };
}

function CartaoForaDoAr({ serie, nomesContas }) {
  const { chaves, nomeDaChave, dados, opcoes, descricao } = useGraficoForaDoAr(serie, nomesContas);
  const linhas = [...serie].reverse();
  return (
    <CartaoGrafico
      titulo="Anúncios fora do ar por conta"
      subtitulo="Produtos com estoque e sem bloqueio que deveriam estar ativos no Mercado Livre."
      grafico={<Line data={dados} options={opcoes} role="img" aria-label={descricao} />}
      tabela={(
        <Table size="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
          <Thead position="sticky" top={0} bg="white"><Tr><Th>Dia</Th>{chaves.map((k) => <Th key={k} isNumeric>{nomeDaChave(k)}</Th>)}</Tr></Thead>
          <Tbody>
            {linhas.map((d) => (
              <Tr key={d.dia}><Td>{diaBR(d.dia)}</Td>{chaves.map((k) => <Td key={k} isNumeric>{num(d[k])}</Td>)}</Tr>
            ))}
          </Tbody>
        </Table>
      )}
    />
  );
}

function CartaoConta({ c, rotulos, nomesContas, curaColeta, coletaFreada, somentePausados, isAdmin, onVerItens }) {
  const nome = nomesContas?.[String(c.conta_id)] || c.nome || `Conta ${c.conta_id}`;
  const porTipo = Object.entries(c.por_tipo || {})
    .map(([chave, total]) => ({ chave, total: Number(total) || 0 }))
    .filter((t) => t.total > 0)
    .sort((a, b) => b.total - a.total);
  // Ativos com entrega a combinar no ML; varredura anterior a essa contagem manda null e a linha não aparece.
  // Sem o estado da cura no painel, só o número.
  const aCombinar = Number.isFinite(c.a_combinar) ? c.a_combinar : null;
  // Com o freio ligado a dica de "tenta mudar" contradiria o aviso logo abaixo.
  const dicaColeta = !aCombinar || typeof curaColeta !== "boolean" || (curaColeta && coletaFreada)
    ? ""
    : curaColeta
      ? " · o sistema tenta mudar para coleta"
      : isAdmin ? " · ligue “Tentar envio por coleta” na Configuração" : " · “Tentar envio por coleta” está desligada";
  return (
    <Cartao>
      <Flex justify="space-between" align="flex-start" gap={2} mb={3} flexWrap="wrap">
        <HStack spacing={2} minW={0}>
          <Text as="h2" fontWeight="bold" color="gray.800" noOfLines={1}>{nome}</Text>
          {somentePausados && <SeloSomentePausados flexShrink={0} />}
        </HStack>
        <Text fontSize="xs" color="gray.600">varrida {dataHoraBR(c.varrido_em)}</Text>
      </Flex>
      {/* Nesse modo a Saúde olha só os itens liberados: o universo da conta é só deles. */}
      {somentePausados && (
        <Text fontSize="xs" color="orange.700" mt={-1} mb={3} role="status">
          Anúncio novo nasce pausado; a Saúde cuida só dos itens liberados (já venderam sem furo, fora LNG/BL PARTS, estoque ≥ 2).
        </Text>
      )}
      <SimpleGrid columns={3} spacing={2}>
        <Info rotulo="Universo">
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="gray.800">{num(c.universo)}</Text>
          <Text fontSize="2xs" color="gray.600" fontWeight="normal">deveriam estar ativos</Text>
        </Info>
        <Info rotulo="Ativos">
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={VERDE}>{pct(c.pct_ativos, 1)}</Text>
          <Text fontSize="2xs" color="gray.600" fontWeight="normal">{num(c.ativos)}</Text>
        </Info>
        <Info rotulo="Fora do ar">
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={Number(c.fora_do_ar) > 0 ? VERMELHO : "gray.800"}>{num(c.fora_do_ar)}</Text>
        </Info>
      </SimpleGrid>
      <Progress value={Math.max(0, Math.min(100, Number(c.pct_ativos) || 0))} size="sm" colorScheme="green" bg="green.100"
        borderRadius="full" mt={3} aria-label={`${pct(c.pct_ativos, 1)} dos anúncios ativos em ${nome}`} />
      {aCombinar !== null && (
        <Text fontSize="xs" color="gray.600" mt={2}>
          Entrega a combinar no ML:{" "}
          <Text as="span" fontWeight="semibold" color="gray.800">{num(aCombinar)} {aCombinar === 1 ? "ativo" : "ativos"}</Text>
          {dicaColeta}
        </Text>
      )}
      {curaColeta && coletaFreada && (
        <Text fontSize="xs" color="orange.700" mt={aCombinar !== null ? 1 : 2} role="status">
          Troca para coleta parada nesta conta: o Mercado Livre acusou troca de categoria por frete nos últimos 7 dias.
        </Text>
      )}
      <Text fontSize="xs" fontWeight="bold" color="gray.700" mt={4} mb={1}>Problemas por tipo</Text>
      <ListaBarras
        itens={porTipo}
        rotuloDe={(k) => rotulo(rotulos, "tipos", k)}
        onVer={(tipo) => onVerItens({ conta: String(c.conta_id), tipo })}
        vazio={c.varrido_em
          ? "Nenhum problema encontrado na última varredura."
          : somentePausados
            ? "Sem varredura: em “só pausados” a Saúde não varre esta conta."
            : "Esta conta ainda não foi varrida."}
      />
    </Cartao>
  );
}

/** Estado vazio coerente com o que está acontecendo (varrendo, pedida, desligado ou esperando a automática). */
function vazioSemVarredura(status, config, isAdmin) {
  if (status.varredura_em_andamento) {
    return { titulo: "Primeira varredura em andamento", texto: "Os números aparecem quando ela terminar (uns 15 minutos por conta).", rodando: true };
  }
  if (status.varredura_solicitada_em) {
    return {
      titulo: "Varredura pedida",
      texto: `Pedida em ${dataHoraBR(status.varredura_solicitada_em)}: começa em até 10 minutos e os números aparecem quando ela terminar.`,
      rodando: true,
    };
  }
  if (!config.ativo) {
    return {
      titulo: "Ainda não houve varredura",
      texto: isAdmin
        ? "Com o sistema desligado não há varredura automática: use “Varrer agora” para o primeiro diagnóstico."
        : "Com o sistema desligado não há varredura automática: peça a um administrador para varrer.",
    };
  }
  return {
    titulo: "Ainda não houve varredura",
    texto: `Os números aparecem depois da primeira varredura. ${isAdmin ? "Use “Varrer agora” ou espere" : "Espere"} a automática`
      + `${status.proxima_varredura_em ? ` (${dataHoraBR(status.proxima_varredura_em)})` : ""}.`,
  };
}

export default function PainelTab({
  painel, carregando, erro, atualizadoEm, dias, diasDados, onDias, onAtualizar, isAdmin, rotulos, contas, nomesContas,
  somentePausadas, onVerItens, onIrConfig,
}) {
  const toast = useToast();
  const montado = useMontado();
  const [confirmarVarrer, setConfirmarVarrer] = useState(false);
  const [contaVarrer, setContaVarrer] = useState("");
  const [varrendo, setVarrendo] = useState(false);
  const [retomando, setRetomando] = useState(false);

  // "Só pausados" pelo painel (relido a cada 30 s): liberar em outro aparelho já reflete aqui.
  const pausadas = useMemo(() => contasSomentePausadas(painel) ?? somentePausadas, [painel, somentePausadas]);
  const contaPausada = (c) => (typeof c.somente_pausados === "boolean" ? c.somente_pausados : !!pausadas?.includes(Number(c.conta_id)));

  const serie = useMemo(() => painel?.serie || [], [painel]);
  const serieForaDoAr = useMemo(() => painel?.serie_fora_do_ar || [], [painel]);
  const temAtividade = useMemo(
    () => serie.some((d) => SERIES_DIA.some(({ chave }) => Number(d[chave] || 0) > 0)),
    [serie]
  );

  const varrer = async () => {
    setVarrendo(true);
    try {
      const r = await varrerSaudeAnuncios({ conta: contaVarrer ? Number(contaVarrer) : null });
      toast({ title: r?.message || "Varredura pedida. Começa em até 10 minutos.", status: "success", duration: 6000, isClosable: true });
      if (montado.current) setConfirmarVarrer(false);
      onAtualizar({ silencioso: true });
    } catch (e) {
      toast(toastDeFalha(e, "Não foi possível pedir a varredura"));
    } finally {
      if (montado.current) setVarrendo(false);
    }
  };

  const retomarIa = async () => {
    setRetomando(true);
    try {
      const r = await retomarIaSaudeAnuncios();
      toast({ title: r?.message || "IA retomada.", status: "success", duration: 5000, isClosable: true });
      onAtualizar({ silencioso: true });
    } catch (e) {
      toast(toastDeFalha(e, "Não foi possível retomar a IA"));
    } finally {
      if (montado.current) setRetomando(false);
    }
  };

  if (!painel) {
    return (
      <Box p={{ base: 3, md: 4 }}>
        {erro
          ? <ErroCarga titulo="Não foi possível carregar o painel." onTentar={() => onAtualizar()} carregando={carregando} />
          : <Center py={16}><Spinner color={ACCENT} /></Center>}
      </Box>
    );
  }

  const config = painel.config || {};
  const status = painel.status || {};
  // Tetos do dia por conta; a troca para coleta só aparece com a cura ligada.
  const usosDoDia = [
    status.recriacoes_hoje && { titulo: "Recriações hoje", uso: status.recriacoes_hoje, maximo: status.max_recriacoes_dia },
    status.exclusoes_hoje && { titulo: "Exclusões hoje", uso: status.exclusoes_hoje, maximo: status.max_exclusoes_dia },
    config.cura_coleta && status.coletas_hoje && { titulo: "Trocas para coleta hoje", uso: status.coletas_hoje, maximo: status.max_coleta_dia },
  ].filter(Boolean);
  const totais = painel.totais || {};
  const listaContas = painel.contas || [];
  const openrouter = status.openrouter || {};
  const pausada = iaPausada(config);
  const porMotivo = (painel.por_motivo || []).map((m) => ({ chave: m.motivo_codigo, total: m.total })).filter((m) => m.chave);
  const porCura = (painel.por_cura || []).map((m) => ({ chave: m.cura, total: m.total })).filter((m) => m.chave);
  const semDiagnostico = painel.sem_diagnostico || {};
  // Os números na tela são do período que chegou; o botão já mostra o novo enquanto ele carrega.
  const periodo = diasDados ?? dias;
  const trocandoPeriodo = periodo !== dias;
  const nomeDias = `em ${periodo} dias`;
  const vazio = vazioSemVarredura(status, config, isAdmin);

  return (
    <Box p={{ base: 3, md: 4 }}>
      <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} gap={2} mb={3} direction={{ base: "column", sm: "row" }} flexWrap="wrap">
        <HStack spacing={1} role="group" aria-label="Período">
          {PERIODOS.map((p) => (
            <Button key={p.dias} size={{ base: "sm", lg: "xs" }} minH={{ base: "40px", lg: "auto" }} flex={{ base: 1, sm: "none" }} aria-pressed={dias === p.dias}
              variant={dias === p.dias ? "solid" : "outline"} bg={dias === p.dias ? undefined : "white"}
              colorScheme={dias === p.dias ? "orange" : "gray"} onClick={() => onDias(p.dias)}>
              {p.label}
            </Button>
          ))}
        </HStack>
        <HStack spacing={2}>
          <Button size={{ base: "md", lg: "sm" }} flex={{ base: 1, sm: "none" }} variant="outline" leftIcon={<RefreshCw size={14} />}
            onClick={() => onAtualizar()} isLoading={carregando} loadingText="Atualizando">
            Atualizar
          </Button>
          {isAdmin && (
            <Button size={{ base: "md", lg: "sm" }} flex={{ base: 1, sm: "none" }} leftIcon={<ScanSearch size={14} />}
              bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} onClick={() => setConfirmarVarrer(true)}
              isDisabled={!!status.varredura_em_andamento}>
              Varrer agora
            </Button>
          )}
        </HStack>
      </Flex>

      {erro && (
        <Alert status="warning" mb={3} borderRadius="md" fontSize="sm">
          <AlertIcon />
          Não foi possível atualizar agora. Mostrando os dados de {atualizadoEm ? atualizadoEm.toLocaleTimeString("pt-BR") : "antes"}.
        </Alert>
      )}

      <VStack align="stretch" spacing={3} mb={4}>
        {!config.ativo && (
          <Alert status="warning" borderRadius="md" fontSize="sm" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <AlertIcon />
            <Box flex="1" minW="200px">
              <AlertTitle fontSize="sm">Desligado: o ciclo automático não roda.</AlertTitle>
              <AlertDescription display="block">
                “Corrigir agora” em um anúncio ainda funciona (respeita a simulação). A varredura diária e a leitura do motivo
                das moderações também ficam paradas.
              </AlertDescription>
            </Box>
            {isAdmin && onIrConfig && (
              <Button size={{ base: "md", lg: "sm" }} variant="outline" bg="white" onClick={onIrConfig}>Abrir configurações</Button>
            )}
          </Alert>
        )}
        {config.simulacao && (
          <Alert status="info" borderRadius="md" fontSize="sm" alignItems="flex-start">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Simulação — só registra o que faria.</AlertTitle>
              <AlertDescription display="block">Nada muda no Mercado Livre. Cada decisão aparece no Histórico como “simulado”.</AlertDescription>
            </Box>
          </Alert>
        )}
        {pausada && (
          <Alert status="error" borderRadius="md" fontSize="sm" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <AlertIcon />
            <Box flex="1" minW="200px">
              <AlertTitle fontSize="sm">IA pausada até {dataHoraBR(config.ia_pausada_ate)}.</AlertTitle>
              <AlertDescription display="block">
                A OpenRouter recusou por falta de saldo. Saldo agora: {openrouter.saldo_usd !== null && openrouter.saldo_usd !== undefined ? usd(openrouter.saldo_usd, 2) : "indisponível"}.
                {config.ia_pausa_motivo ? ` (${config.ia_pausa_motivo})` : ""}
              </AlertDescription>
            </Box>
            {isAdmin && (
              <Button size={{ base: "md", lg: "sm" }} colorScheme="red" onClick={retomarIa} isLoading={retomando}>Retomar IA</Button>
            )}
          </Alert>
        )}
        {openrouter.erro && (
          <Alert status="warning" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {openrouter.erro}
          </Alert>
        )}
      </VStack>

      <Cartao
        titulo="Agora"
        mb={4}
        extra={atualizadoEm && (
          <Text fontSize="xs" color="gray.600">Atualizado às {atualizadoEm.toLocaleTimeString("pt-BR")} · a cada 30 s</Text>
        )}
      >
        <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} spacing={{ base: 3, md: 4 }}>
          <Info rotulo="Última varredura">{status.ultima_varredura_em ? dataHoraBR(status.ultima_varredura_em) : "Nunca"}</Info>
          <Info rotulo="Varredura">
            {status.varredura_em_andamento ? (
              <HStack spacing={1.5}><Spinner size="xs" color={AZUL} /><Text>Em andamento</Text></HStack>
            ) : status.varredura_solicitada_em ? (
              <Text>Pedida em {dataHoraBR(status.varredura_solicitada_em)}</Text>
            ) : status.proxima_varredura_em ? (
              <Text>Próxima {dataHoraBR(status.proxima_varredura_em)}</Text>
            ) : (
              <Text fontWeight="normal" color="gray.600">{config.ativo ? "—" : "Automática desligada"}</Text>
            )}
          </Info>
          <Info rotulo="Na fila">
            {status.jobs_na_fila === null || status.jobs_na_fila === undefined
              ? "—"
              : `${num(status.jobs_na_fila)} ${Number(status.jobs_na_fila) === 1 ? "tarefa" : "tarefas"}`}
          </Info>
          <Info rotulo="Saldo OpenRouter">
            {openrouter.saldo_usd !== null && openrouter.saldo_usd !== undefined
              ? usd(openrouter.saldo_usd, 2)
              : <Text as="span" color="gray.600" fontWeight="normal">indisponível</Text>}
          </Info>
          <Info rotulo="IA hoje">
            <Text>{usd(status.ia_gasto_hoje_usd)} <Text as="span" color="gray.600" fontWeight="normal">de {usd(status.ia_teto_diario_usd, 2)}</Text></Text>
            <Medidor valor={status.ia_gasto_hoje_usd} maximo={Number(status.ia_teto_diario_usd) || 0} rotulo="Gasto de IA hoje em relação ao teto diário" />
          </Info>
        </SimpleGrid>
        {usosDoDia.length > 0 && (
          <SimpleGrid columns={{ base: 1, md: 2, lg: usosDoDia.length }} spacing={{ base: 3, md: 6 }} mt={4} pt={3} borderTop="1px solid" borderColor="gray.100">
            {usosDoDia.map((u) => (
              <UsoPorConta key={u.titulo} titulo={u.titulo} uso={u.uso} maximo={u.maximo} nomesContas={nomesContas} />
            ))}
          </SimpleGrid>
        )}
      </Cartao>

      {listaContas.length === 0 ? (
        <Cartao>
          <Vazio titulo={vazio.titulo} acao={vazio.rodando ? <Spinner color={ACCENT} /> : null}>{vazio.texto}</Vazio>
        </Cartao>
      ) : (
        <Box opacity={trocandoPeriodo ? 0.55 : 1} transition="opacity .15s" aria-busy={trocandoPeriodo}>
          <SimpleGrid columns={{ base: 1, lg: Math.min(2, listaContas.length), xl: Math.min(3, listaContas.length) }} spacing={3} mb={4}>
            {listaContas.map((c) => (
              <CartaoConta key={c.conta_id} c={c} rotulos={rotulos} nomesContas={nomesContas} curaColeta={config.cura_coleta}
                coletaFreada={!!status.coleta_freada?.[String(c.conta_id)]} somentePausados={contaPausada(c)}
                isAdmin={isAdmin} onVerItens={onVerItens} />
            ))}
          </SimpleGrid>

          <Text as="h2" fontWeight="bold" fontSize="sm" color="gray.800" mb={2}>Totais</Text>
          <SimpleGrid columns={{ base: 2, md: 4, "2xl": 8 }} spacing={3} mb={4}>
            <Kpi label="Pendentes" valor={num(totais.pendentes)} ajuda="agora" cor="#4A5568" />
            <Kpi label="Em andamento" valor={num(totais.em_andamento)} ajuda="agora" cor={AZUL} />
            <Kpi label="Corrigidos" valor={num(totais.corrigidos)} ajuda={nomeDias} cor={VERDE} />
            <Kpi label="Recriados" valor={num(totais.recriados)} ajuda={nomeDias} cor={AZUL} />
            <Kpi label="Falhas" valor={num(totais.falhas)} ajuda={nomeDias} cor={VERMELHO} />
            <Kpi label="Revisão humana" valor={num(totais.manuais)} ajuda="agora, precisam de alguém" cor="#C05621" />
            <Kpi label="Ignorados" valor={num(totais.ignorados)} ajuda="agora" cor="#4A5568" />
            <Kpi label="Custo de IA" valor={usd(totais.custo_ia_usd)} ajuda={nomeDias} cor="#6B46C1" />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: serieForaDoAr.length >= 2 ? 2 : 1 }} spacing={3} mb={4}>
            {temAtividade ? (
              <CartaoGrafico
                titulo="Correções por dia"
                subtitulo="Corrigidos, recriados, falhas e simulações (o que faria em simulação)."
                grafico={<GraficoDiario serie={serie} periodo={periodo} />}
                tabela={(
                  <Table size="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    <Thead position="sticky" top={0} bg="white">
                      <Tr><Th>Dia</Th>{SERIES_DIA.map((s) => <Th key={s.chave} isNumeric>{s.label}</Th>)}<Th isNumeric>Custo IA</Th></Tr>
                    </Thead>
                    <Tbody>
                      {[...serie].reverse().map((d) => (
                        <Tr key={d.dia}>
                          <Td>{diaBR(d.dia)}</Td>
                          {SERIES_DIA.map((s) => <Td key={s.chave} isNumeric>{num(d[s.chave] || 0)}</Td>)}
                          <Td isNumeric>{usd(d.custo_ia_usd || 0)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              />
            ) : (
              <Cartao titulo="Correções por dia">
                <Vazio titulo={`Nenhuma correção ${nomeDias}`}>
                  {config.ativo
                    ? "O ciclo automático ainda não tratou nenhum anúncio neste período."
                    : "O ciclo automático está desligado: aqui só aparecem as correções pedidas à mão (“Corrigir agora”)."}
                </Vazio>
              </Cartao>
            )}
            {serieForaDoAr.length >= 2 && <CartaoForaDoAr serie={serieForaDoAr} nomesContas={nomesContas} />}
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
            <Cartao titulo="Por motivo do Mercado Livre">
              <ListaBarras itens={porMotivo} rotuloDe={(k) => rotulo(rotulos, "motivos", k)} onVer={(motivo) => onVerItens({ motivo })}
                vazio={semDiagnostico.motivo ? "Nenhum motivo lido ainda." : "Nenhum anúncio moderado."} />
              <SemDiagnostico n={semDiagnostico.motivo} oQue="moderações sem o motivo lido" ligado={config.ativo} />
            </Cartao>
            <Cartao titulo="Por cura (o que o sistema vai fazer)">
              <ListaBarras itens={porCura} rotuloDe={(k) => rotulo(rotulos, "curas", k)} onVer={(cura) => onVerItens({ cura })}
                vazio={semDiagnostico.cura ? "Nenhuma cura decidida ainda." : "Nenhum problema com cura definida."} />
              <SemDiagnostico n={semDiagnostico.cura} oQue="itens em aberto sem cura decidida" ligado={config.ativo} />
            </Cartao>
          </SimpleGrid>
        </Box>
      )}

      <ConfirmDialog
        isOpen={confirmarVarrer}
        onClose={() => setConfirmarVarrer(false)}
        onConfirm={varrer}
        isLoading={varrendo}
        title="Varrer agora"
        confirmText="Varrer"
        buttonSize={{ base: "md", lg: "sm" }}
        wrapButtons
        body={(
          <VStack align="stretch" spacing={3}>
            <Text>
              Lê os anúncios no Mercado Livre e refaz o diagnóstico em segundo plano. Começa em até 10 minutos.
              A varredura não corrige nada: as correções seguem as Configurações.
            </Text>
            <FormControl>
              <FormLabel fontSize="sm" mb={1}>Conta</FormLabel>
              <Select size={{ base: "md", lg: "sm" }} value={contaVarrer} onChange={(e) => setContaVarrer(e.target.value)}>
                <option value="">Todas as contas atendidas</option>
                {contas.map((c) => {
                  const pausada = !!pausadas?.includes(Number(c.id));
                  return <option key={c.id} value={c.id} disabled={pausada}>{c.nome}{pausada ? " (só pausados)" : ""}</option>;
                })}
              </Select>
            </FormControl>
            {status.varredura_solicitada_em && (
              <Text fontSize="sm" color="orange.700">
                Já há uma varredura pedida em {dataHoraBR(status.varredura_solicitada_em)}: este pedido substitui o anterior.
              </Text>
            )}
          </VStack>
        )}
      />
    </Box>
  );
}
